import { useCallback, useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from 'react-router-dom';
import './App.css';
import logoLight from './images/Meridian Event Tech Logo (2).png';
import logoDark from './images/meridian-logo-dark.png';
import logoMark from './images/meridian-mark.png';
import Home from './pages/Home';
import Team from './pages/Team';
import Work from './pages/Work';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
// Parked until the placeholder quotes are replaced with real ones — see the
// route below. The import goes with it, or CI fails the build on an unused var.
// import Testimonials from './pages/Testimonials';

const THEME_KEY = 'met-theme';

const SITE_NAME = 'Meridian Event Technologies';
const ORIGIN = 'https://meridianeventtech.com';

/* Per-route title and description. index.html carries the home page's copy as
   the static default, which is what link unfurlers and non-rendering crawlers
   read; this swaps it for the real page once React is running, which is what
   Google indexes. */
const ROUTE_META = {
  '/': {
    title: `Event Data Capture & Lead Collection | ${SITE_NAME}`,
    description:
      'Turn event traffic into data you can use. Event data capture and lead collection — badge scanning, live dashboards, and personalized follow-up.',
  },
  '/work': {
    title: `Our Work: Ford & BMW Motorrad Activations | ${SITE_NAME}`,
    description:
      'Three programs, three problems: booking and payments, multi-activation festivals, and measuring a whole show floor as one journey.',
  },
  '/team': {
    title: `Our Team | ${SITE_NAME}`,
    description:
      'The people behind Meridian — decades of combined expertise in event marketing, technology and client service.',
  },
  '/contact': {
    title: `Get in Touch | ${SITE_NAME}`,
    description:
      'Ready to transform your event technology? Tell us about your activation and we will show you what the data can do.',
  },
  '/privacy': {
    title: `Privacy Policy | ${SITE_NAME}`,
    description:
      'How Meridian handles personal information when we run registration, scheduling and email programs on behalf of our clients.',
  },
};

function setMetaContent(attr, name, value) {
  const tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (tag) tag.setAttribute('content', value);
}

/**
 * Keeps the document title, description and canonical URL in step with the
 * route. Note the limit: link unfurlers (Slack, LinkedIn, iMessage) do not run
 * JavaScript, so they always see index.html's static tags no matter which path
 * is pasted. Fixing that for every route needs prerendering, not this.
 */
function RouteMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = ROUTE_META[pathname];
    if (!meta) return;

    document.title = meta.title;
    setMetaContent('name', 'description', meta.description);
    setMetaContent('property', 'og:title', meta.title);
    setMetaContent('property', 'og:description', meta.description);
    setMetaContent('name', 'twitter:title', meta.title);
    setMetaContent('name', 'twitter:description', meta.description);
    setMetaContent('property', 'og:url', `${ORIGIN}${pathname}`);

    const canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${ORIGIN}${pathname}`);
  }, [pathname]);

  return null;
}

// The inline script in public/index.html has already resolved and applied the
// theme before first paint, so read it back off the element rather than
// recomputing it — that keeps React's first render in sync with the DOM.
function readTheme() {
  if (typeof document === 'undefined') return 'light';
  return document.documentElement.getAttribute('data-theme') || 'light';
}

function ThemeToggle() {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Storage unavailable — the theme still applies for this session.
    }
  }, [theme]);

  const toggle = useCallback(
    () => setTheme((current) => (current === 'dark' ? 'light' : 'dark')),
    []
  );

  const next = theme === 'dark' ? 'light' : 'dark';

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
    >
      <span className="theme-toggle-thumb" />
    </button>
  );
}

/**
 * React Router does not manage scroll position on navigation. Without this,
 * following a link from halfway down Home lands you halfway down the next
 * page, and the /work#case-id links from the Home teaser do nothing at all.
 */
function ScrollManager() {
  // `key` changes on every navigation, including a push to the path already
  // shown. Keying off it as well as the path is what makes clicking Home from
  // halfway down Home return to the top, rather than doing nothing.
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    const target = hash ? document.querySelector(hash) : null;

    if (!target) {
      // `behavior: 'instant'` matters: `html` sets `scroll-behavior: smooth`,
      // which would otherwise turn this into a second-long animated glide up
      // through the whole page. That animation is cancelled by any wheel or
      // touch input — including momentum left over from the scrolling that
      // came just before the click — leaving the reader stranded mid-page.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return undefined;
    }

    // Aligning once is not enough. The page keeps growing after the first
    // scroll — webfonts swap in and reflow every heading, and images decode —
    // so the target drifts downward and you land in the gap after it. Realign
    // as each of those settles, but stop the moment the reader scrolls, so we
    // never yank the page out from under them.
    let cancelled = false;
    const align = () => {
      if (!cancelled) {
        target.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    };
    const cancel = () => {
      cancelled = true;
    };

    const passive = { passive: true };
    window.addEventListener('wheel', cancel, passive);
    window.addEventListener('touchstart', cancel, passive);
    window.addEventListener('keydown', cancel);

    align();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(align);
    }

    const pending = Array.from(document.images).filter((img) => !img.complete);
    pending.forEach((img) => {
      img.addEventListener('load', align);
      img.addEventListener('error', align);
    });

    const settle = window.setTimeout(align, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(settle);
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('keydown', cancel);
      pending.forEach((img) => {
        img.removeEventListener('load', align);
        img.removeEventListener('error', align);
      });
    };
  }, [pathname, hash, key]);

  return null;
}

function App() {
  return (
    <Router>
      <ScrollManager />
      <RouteMeta />
      <div className="App">
        {/* Navigation */}
        <nav className="navbar">
          <div className="nav-container">
            <Link to="/" className="nav-brand">
              {/* Full lockup on wider screens; below that there isn't room for
                  the wordmark alongside four links and the toggle, so the mark
                  stands in rather than the lockup being squashed. */}
              <span className="nav-lockup">
                <img
                  src={logoLight}
                  alt="Meridian Event Technologies"
                  className="nav-logo nav-logo--light"
                />
                <img
                  src={logoDark}
                  alt=""
                  aria-hidden="true"
                  className="nav-logo nav-logo--dark"
                />
              </span>
              <img
                src={logoMark}
                alt="Meridian Event Technologies"
                className="nav-mark"
              />
            </Link>
            <div className="nav-links">
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/work" className="nav-link">Work</Link>
              <Link to="/team" className="nav-link">Team</Link>
              {/* <Link to="/testimonials" className="nav-link">Testimonials</Link> */}
              <Link to="/contact" className="nav-link">Contact</Link>
              <ThemeToggle />
            </div>
          </div>
        </nav>

        {/* Routes */}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/work" element={<Work />} />
          <Route path="/team" element={<Team />} />
          {/* Parked: the quotes on this page are generated placeholders
              attributed to named people at real companies. The nav link was
              already commented out, but the route kept it reachable by direct
              URL. Restore this and the import above once the testimonials are
              real. */}
          {/* <Route path="/testimonials" element={<Testimonials />} /> */}
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
        </Routes>

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <p>&copy; 2026 Meridian Event Technologies. All rights reserved.</p>
            <p>
              <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            </p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
